import * as React from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  BuildJsonType,
  EnhancedAppointment,
  VirtualTourJsonType,
} from '@bb/domain/dist/types';
import { ILocationService } from 'angular';
import errorReporter from '@app/errorReporter';

import { CalendarSyncButton } from '../../DashboardComponents/AppointmentDetailViewComponents';
import CalendarSyncProviderModal from '../../DashboardComponents/CalendarSyncProviderModal';

import AngularLocationServiceContext from './AngularLocationServiceContext';

type CoopCalendarSyncButtonProps = {
  appointment?: BuildJsonType<EnhancedAppointment>;
  virtualTour?: VirtualTourJsonType;
  almanacUrl?: string;
  retsId?: string;
  dashboardId?: string;
  isTour?: boolean;
  $location?: ILocationService;
  newDetailsPageDesign?: boolean;
  queryParams?: Record<string, string>;
};

type CoopCalendarSyncButtonState = {
  isVisible: boolean;
  isAuthenticated?: boolean;
  isModalOpen: boolean;
  isSyncButtonClicked: boolean;
  existingAlmanacEvent?: boolean;
};

const defaultUrl = '/user/coop/almanac';

class CoopCalendarSyncButton extends React.Component<
  CoopCalendarSyncButtonProps,
  CoopCalendarSyncButtonState
> {
  static contextType = AngularLocationServiceContext;

  declare context: React.ContextType<typeof AngularLocationServiceContext>;

  constructor(props: CoopCalendarSyncButtonProps) {
    super(props);

    this.state = {
      isVisible: false,
      isModalOpen: false,
      isSyncButtonClicked: false,
    };
  }

  async componentDidMount() {
    const {
      appointment,
      virtualTour,
      almanacUrl = defaultUrl,
      isTour,
    } = this.props;

    const queryParams =
      this.props.queryParams ||
      (this.context.$location || this.props.$location)?.search();

    try {
      const params: Record<string, string> = {};
      if (isTour) {
        params.virtualTourId = virtualTour.id;
      } else {
        params.appointmentId = appointment.id;
      }

      const [
        {
          data: { isAuthenticated },
        },
        {
          data: { eventId },
        },
      ] = await Promise.all([
        axios.get<{ isAuthenticated: boolean; email: string }>(
          `${almanacUrl}/auth_status`,
        ),
        axios.get<{ eventId: string }>(`${almanacUrl}/events`, {
          params,
        }),
      ]);

      let existingAlmanacEvent = !!eventId;

      if (queryParams.action === 'sync' && !existingAlmanacEvent) {
        await this.onCalendarSyncButtonClick();
        existingAlmanacEvent = true;
      } else if (queryParams.sync === 'emaillink') {
        if (isAuthenticated) {
          await this.onCalendarSyncButtonClick();
          await this.showCalendarSyncSuccessModal();
          existingAlmanacEvent = true;
        } else if (existingAlmanacEvent) {
          await this.showCalendarSyncSuccessModal();
        } else {
          this.startOauth();
        }
      }

      this.setState({
        isAuthenticated,
        existingAlmanacEvent,
        isVisible: true,
      });
    } catch (err) {
      Swal.fire({
        title: 'Error with calendar sync',
        text: "Something happened and we weren't able to connect to the calendar",
        type: 'error',
      });

      errorReporter.error(err);
      this.setState({ isVisible: false });
    }
  }

  showCalendarSyncSuccessModal = async () => {
    const { isTour } = this.props;
    await Swal.fire({
      title: `${isTour ? 'Virtual Showing' : 'Showing'} Synced!`,
      text: `The ${
        isTour ? 'virtual showing' : 'showing'
      } event should now appear in your calendar`,
      type: 'success',
      confirmButtonText: 'Ok',
      showCancelButton: false,
    });
  };

  onCalendarSyncButtonClick = async () => {
    const {
      appointment,
      virtualTour,
      almanacUrl = defaultUrl,
      isTour,
    } = this.props;
    this.setState({ isSyncButtonClicked: true });

    try {
      const body: Record<string, string> = {};
      if (isTour) {
        body.virtualTourId = virtualTour.id;
      } else {
        body.appointmentId = appointment.id;
      }

      await axios.post(`${almanacUrl}${isTour ? '/tour' : ''}/events`, body);

      this.setState({
        existingAlmanacEvent: true,
      });
    } catch (err) {
      errorReporter.error(err);
      this.setState({
        existingAlmanacEvent: false,
      });
    } finally {
      this.setState({ isSyncButtonClicked: false });
    }
  };

  startOauth = () => {
    this.setState({ isModalOpen: true });
  };

  closeModal = () => {
    this.setState({ isModalOpen: false });
  };

  redirectUser = (service: string) => {
    const queryParams =
      this.props.queryParams &&
      Object.fromEntries(
        Object.entries(this.props.queryParams).filter(([key]) => !!key),
      );

    const hasQueryParam =
      Object.keys(
        queryParams ||
          (this.context.$location || this.props.$location)?.search(),
      ).length !== 0;

    const { retsId, dashboardId } = this.props;
    let dashboardType = '';
    if (retsId) {
      dashboardType = `&retsId=${retsId}`;
    } else if (dashboardId) {
      dashboardType = `&dashboardId=${dashboardId}`;
    }

    const redirectUrl = `/almanac/connect?service=${service}${dashboardType}&redirectUrl=${
      // This is pretty brittle, but the URL api doesn't play nice with angular's hash routing
      encodeURIComponent(
        `${window.location.href}${hasQueryParam ? '&' : '?'}action=sync`,
      )
    }`;

    window.location.href = redirectUrl;
  };

  render() {
    const {
      isAuthenticated,
      isSyncButtonClicked,
      existingAlmanacEvent,
      isVisible,
      isModalOpen,
    } = this.state;
    const marginTop = this.props.newDetailsPageDesign ? '0px' : '10px';
    return isVisible ? (
      <>
        <div style={{ marginTop }}>
          <CalendarSyncButton
            isAlmanacAuthenticated={isAuthenticated}
            isSyncButtonClicked={isSyncButtonClicked}
            existingAlmanacEvent={existingAlmanacEvent}
            startOauth={this.startOauth}
            onCalendarSyncButtonClick={this.onCalendarSyncButtonClick}
            newDetailsPageDesign={this.props.newDetailsPageDesign}
          />
        </div>
        <CalendarSyncProviderModal
          showModal={isModalOpen}
          closeModal={this.closeModal}
          redirectUser={this.redirectUser}
        />
      </>
    ) : null;
  }
}

export default CoopCalendarSyncButton;
